import React, { useEffect } from 'react';
import { Box, Group, Loader } from '@mantine/core';
import { useLocation, useSearchParams } from 'react-router-dom';
import qs from 'query-string';
import { sendEmailVerification } from 'firebase/auth';
import { useNotifications } from '@mantine/notifications';

import { useIsMobileMedia, useNextPath, useRequestStates, useUser } from '~/hooks';
import { getFirebaseAuth } from '~/lib/firebase';
import userSlice, { useLogoutMutation, useVerifyEmailMutation } from '~/redux/slices/userSlice';
import { authContextSlice, useAuthContext } from '~/pages/LandingPage/contexts/AuthContext';

import { LabelMediumStrong, ParagraphMedium, ParagraphSmall } from '../../../typography';
import * as Styles from '../AuthModal.styled';

const NOTIFICATION_TIMEOUT = 5000;

const VerifyEmailView = () => {
  const user = useUser();
  const [getUser] = userSlice.endpoints.getUser.useLazyQuery();
  const isMobileMedia = useIsMobileMedia();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');
  const nextPath = useNextPath('/candidates/browse/');
  const { showNotification } = useNotifications();
  const [verifyEmail, verifyEmailResult] = useVerifyEmailMutation();
  const [logoutUser] = useLogoutMutation();
  const [sendEmailVerificationReqStates, sendEmailVerificationReqHandlers] = useRequestStates();
  const [retryRegistrationReqStates, retryRegistrationReqHandlers] = useRequestStates();

  const { dispatchAuthCtx } = useAuthContext();

  const handleRetryRegisterClick = () => {
    retryRegistrationReqHandlers.setPending();
    const auth = getFirebaseAuth();
    auth.signOut();
    logoutUser();
    dispatchAuthCtx(authContextSlice.actions.setView('signup'));
    retryRegistrationReqHandlers.setFulfilled();
  };

  const handleResendVerificationLinkClick = () => {
    sendEmailVerificationLink();
  };

  const sendEmailVerificationLink = async (upraisedUser = user) => {
    sendEmailVerificationReqHandlers.setPending();

    const lastSentTimestamp = sendEmailVerificationReqStates.data?.timestamp;
    if (lastSentTimestamp) {
      // Refetch user to check if the email is already verified before every resend attempt
      const resp = await getUser();
      if (resp.data?.user.isEmailVerified) {
        sendEmailVerificationReqHandlers.setFulfilled();
        return;
        // Do nothing. The page will auto reload as the user.isEmailVerified changes
        // This is controlled by a landing page effect
      }
    }

    const firebaseUser = getFirebaseAuth().currentUser;
    if (!firebaseUser || !upraisedUser) {
      sendEmailVerificationReqHandlers.setFulfilled();
      return;
    }

    try {
      const searchParamsObj = [...searchParams.entries()].reduce((a, [k, v]) => ({ ...a, [k]: v }), {});
      const queryString = qs.stringify({
        ...searchParamsObj,
        mode: 'verify-email',
        email: upraisedUser.email,
        token: upraisedUser.emailVerificationToken,
        next_path: nextPath,
      });
      const actionCodeSettings = {
        url: `https://${window.location.host}/auth?${queryString}`,
      };
      await sendEmailVerification(firebaseUser, actionCodeSettings);
      showNotification({
        color: 'teal',
        title: 'Verification Link Sent',
        message: 'A verification link has been sent to your registered email address.',
        autoClose: NOTIFICATION_TIMEOUT,
      });
      sendEmailVerificationReqHandlers.setFulfilled({ timestamp: Date.now() });
    } catch (e) {
      if (e.code === 'auth/too-many-requests') {
        showNotification({
          color: 'red',
          title: 'Error',
          message: 'Too many attempts. Please try again after sometime.',
          autoClose: NOTIFICATION_TIMEOUT,
        });
        sendEmailVerificationReqHandlers.setFulfilled({ timestamp: Date.now() });
        return;
      }
      sendEmailVerificationReqHandlers.setRejected();
    }
  };

  const verifyUpraisedEmail = async () => {
    const email = searchParams.get('email');
    const token = searchParams.get('token');
    if (!email || !token) {
      showNotification({
        color: 'red',
        title: 'Error',
        message: 'Unable to verify your email. Missing verification token.',
        autoClose: NOTIFICATION_TIMEOUT,
      });
      return;
    }

    const resp = await verifyEmail({ email, token });
    if (resp.error) {
      showNotification({
        color: 'red',
        title: 'Error',
        message: 'Failed to verify email address. Please try again',
        autoClose: NOTIFICATION_TIMEOUT,
      });
    }
    /**
     * On success, this mutation will invalidate the User tag.
     * Hence the Upraised user will be fetched again.
     * When user.isEmailVerified is set, this page will
     * auto-redirect to the nextPath
     */
  };

  useEffect(() => {
    if (location.state) {
      const { sendVerificationEmail, upraisedUser } = location.state;
      if (sendVerificationEmail && upraisedUser) {
        sendEmailVerificationLink(upraisedUser);
      }
    }
  }, [location.state]);

  useEffect(() => {
    if (mode === 'verify-email') {
      verifyUpraisedEmail();
    }
  }, [mode]);

  if (verifyEmailResult.isLoading) {
    return (
      <Group align="center" position="center">
        <Loader variant="bars" size="sm" />
        <ParagraphMedium>Verifying email...</ParagraphMedium>
      </Group>
    );
  } else if (verifyEmailResult.isError) {
    return (
      <Group direction="column" align="center">
        <ParagraphMedium>Failed to verify email</ParagraphMedium>
        <Styles.PrimaryBtn onClick={verifyUpraisedEmail}>
          <LabelMediumStrong color="#fff" style={{ width: '100%' }}>
            Try Again
          </LabelMediumStrong>
        </Styles.PrimaryBtn>
      </Group>
    );
  }

  return (
    <Box>
      {user ? (
        <ParagraphMedium mt={isMobileMedia ? '24px' : '32px'} color="#434343">
          Open the e-mail verification link we have sent on <strong>{user.email}</strong> to activate your account.
        </ParagraphMedium>
      ) : null}

      <ParagraphSmall mt="8px" color="#626262">
        Note: It usually takes 2-3 minutes to receive the e-mail.
      </ParagraphSmall>

      <LabelMediumStrong mt="16px" color="#222">
        Still can't find an email from us in your inbox?
      </LabelMediumStrong>

      <ParagraphMedium mt="8px" color="#434343">
        <ol style={{ paddingInlineStart: '20px' }}>
          <li> Check your spam folder, sometimes things get lost there</li>
          <li> You might have entered an incorrect e-mail address because of a typo that you didn't anticipate</li>
          <li> We can't deliver to your e-mail address because of corporate firewalls or filtering</li>
        </ol>
      </ParagraphMedium>
      <Group grow spacing="12px" mt={isMobileMedia ? '24px' : '32px'}>
        <Styles.OutlinedBtn loading={retryRegistrationReqStates.isPending}>
          <LabelMediumStrong color="#222" style={{ flex: 1 }} onClick={handleRetryRegisterClick}>
            Retry with another e-mail
          </LabelMediumStrong>
        </Styles.OutlinedBtn>
        <Styles.PrimaryBtn
          onClick={handleResendVerificationLinkClick}
          loading={sendEmailVerificationReqStates.isPending}
        >
          <LabelMediumStrong color="#fff" style={{ flex: 1 }}>
            Resend Verification Link
          </LabelMediumStrong>
        </Styles.PrimaryBtn>
      </Group>
    </Box>
  );
};

export default VerifyEmailView;
